import type { ApolloError } from '@apollo/client/errors';
import type { GraphQLError } from 'graphql/error/GraphQLError';
import stringify from 'json-stable-stringify';
import { useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import type { CartLineItem } from 'src/app/state/types/cart';
import { log } from 'src/app/utils/logging';
import {
  type CreateOrderInput,
  type CreateOrderWithPromotionMutationResult,
  type ProductSet,
  useCreateOrderWithPromotionMutation,
} from 'src/graphql/generated';
import type AppState from 'src/types/appState';

import {
  mapCartLineItemsWithNewProductSetId,
  mapCartLineItemToOrderLineItem,
} from './mappers';

// the order cache holds the hash string for the cart line items and their corresponding orderId
// to avoid creating multiple orders for the same line items
const orderCache = new Map<string, string>();

class InvalidPromoCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidPromoCodeError';
  }
}

const useCreateOrderWithProductSet = (
  createProductSet: () => Promise<{
    productSet: ProductSet | undefined;
    error: unknown;
  }>,
) => {
  const { advertiserNation, interactionId, seekAdvertiserId } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      interactionId: state.session.interactionId,
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );

  const [createOrderWithPromotionMutation] =
    useCreateOrderWithPromotionMutation();

  return useCallback(
    async (
      requestCartLineItems: CartLineItem[],
      requestProductSetId: string,
      requestedPromoCode?: string,
    ): Promise<{
      orderId?: string;
      error?: Error;
      promoCode?: string;
      orderNumber?: string;
    }> => {
      const createOrder = async (
        cartLineItems: CartLineItem[],
        productSetId: string,
        promoCode?: string,
      ): Promise<{
        orderId?: string;
        error?: Error;
        promoCode?: string;
        orderNumber?: string;
      }> => {
        const isCartInputIncomplete =
          cartLineItems.length === 0 ||
          cartLineItems.some((item) => item.productSetId !== productSetId);

        if (!seekAdvertiserId || isCartInputIncomplete) {
          return {
            orderId: undefined,
            error: undefined,
          };
        }

        // TODO: remove this temporary mitigation to send multiple ad product line items to create order
        // after premium upsell issue is resolved or premium upsell is killed altogether after experiment
        // https://myseek.atlassian.net/browse/AT-2752
        if (
          cartLineItems.length === 2 &&
          cartLineItems.every((item) => item.type === 'AdProduct')
        ) {
          log.error(
            'useCreateOrderWithProductSet | createOrder: cannot create order with multiple AdProduct items',
            {
              productSetId,
              seekAdvertiserId,
              cartLineItems,
            },
          );
          const multipleAdProductLineItemsError = new Error(
            'Multiple job ad line item order',
          );
          return {
            orderId: undefined,
            error: multipleAdProductLineItemsError,
          };
        }

        const createOrderContext = {
          headers: {
            site: advertiserNation,
            'interaction-id': interactionId,
          },
        };
        const lineItemsHashKey = stringify({ cartLineItems, promoCode });

        if (orderCache.has(lineItemsHashKey)) {
          return {
            orderId: orderCache.get(lineItemsHashKey),
            promoCode,
            error: undefined,
          };
        }

        const createOrderVariables: { input: CreateOrderInput } = {
          input: {
            advertiserId: seekAdvertiserId,
            advertiserNation,
            promoCode,
            lineItems: cartLineItems.map((cartItem) =>
              mapCartLineItemToOrderLineItem(cartItem),
            ),
            orderCreationId: uuidv4(),
          },
        };

        const { data } = await createOrderWithPromotionMutation({
          context: createOrderContext,
          variables: createOrderVariables,
        });

        if (data) {
          const createOrderWithPromotion = (
            data as CreateOrderWithPromotionMutationResult['data']
          )?.createOrderWithPromotion;

          if (!createOrderWithPromotion) {
            log.error(
              'useCreateOrderWithProductSet | createOrder: failed to create order with promotion',
              {
                productSetId,
                seekAdvertiserId,
                cartLineItems,
              },
            );
            return {
              error: new Error('Failed to create order'),
              promoCode: '',
            };
          }

          if (createOrderWithPromotion.__typename === 'PromotionInvalidError') {
            log.error(
              'useCreateOrderWithProductSet | PromotionInvalidError | createOrder: failed to create order with promotion',
              {
                productSetId,
                seekAdvertiserId,
                cartLineItems,
              },
              true,
            );
            return {
              error: new InvalidPromoCodeError('Invalid promo code'),
              promoCode: '',
            };
          }

          if (
            createOrderWithPromotion.__typename !== 'CreateOrderSuccessPayload'
          ) {
            log.error(
              'useCreateOrderWithProductSet | Other Failure | createOrder: failed to create order with promotion',
              {
                productSetId,
                seekAdvertiserId,
                cartLineItems,
              },
            );
            return {
              error: new Error('Failed to create order'),
              promoCode: '',
            };
          }

          const orderId = createOrderWithPromotion?.order?.orderId;
          const orderNumber = createOrderWithPromotion?.order?.orderNumber;

          if (orderId) {
            orderCache.set(lineItemsHashKey, orderId);
          }

          return {
            orderId,
            orderNumber,
            promoCode: createOrderWithPromotion?.promotion?.code ?? undefined,
            error: undefined,
          };
        }

        return {
          error: new Error('Failed to create order'),
        };
      };

      let requestedProductSetId = requestProductSetId;
      let requestedCartLineItems = requestCartLineItems;

      try {
        return await createOrder(
          requestedCartLineItems,
          requestedProductSetId,
          requestedPromoCode,
        );
      } catch (error) {
        let createOrderError = error as Error;

        const productSetExpiredOrNotFound = (
          error as ApolloError
        ).graphQLErrors?.find(
          (graphQLError: GraphQLError) =>
            graphQLError.extensions?.code === 'PRODUCT_SET_NOT_FOUND' ||
            graphQLError.extensions?.code === 'PRODUCT_SET_EXPIRED',
        );

        if (productSetExpiredOrNotFound) {
          log.error(
            'useCreateOrderWithProductSet | createOrder: recreating product-set as the current one has expired or not found',
            {
              advertiserNation,
              interactionId,
              seekAdvertiserId,
              error: createOrderError,
              productSetId: requestedProductSetId,
              cartLineItems: requestedCartLineItems,
            },
          );

          // retry re-creating order when failed with product set expired error
          try {
            const { productSet: newProductSet } = await createProductSet();

            requestedCartLineItems = mapCartLineItemsWithNewProductSetId(
              requestCartLineItems,
              newProductSet!,
            );
            requestedProductSetId = newProductSet!.id;

            return await createOrder(
              requestedCartLineItems,
              requestedProductSetId,
            );
          } catch (retryError) {
            createOrderError = retryError as Error;
          }
        }

        log.error(
          'useCreateOrderWithProductSet | createOrder: failed to create order using product set',
          {
            advertiserNation,
            interactionId,
            seekAdvertiserId,
            error: createOrderError,
            productSetId: requestedProductSetId,
            cartLineItems: requestedCartLineItems,
          },
        );

        return {
          orderId: undefined,
          orderNumber: undefined,
          promoCode: '',
          error: createOrderError,
        };
      }
    },
    [
      advertiserNation,
      createOrderWithPromotionMutation,
      createProductSet,
      interactionId,
      seekAdvertiserId,
    ],
  );
};

export { useCreateOrderWithProductSet, InvalidPromoCodeError };
